/**
 * Comparaison de texte pour la recherche — la **même** des deux côtés de l'API
 * (`ARCHITECTURE_V2.md` §3.1, étape 4 du §8).
 *
 * ─────────────────────────────────────────────────────────────────────────
 * POURQUOI CE FICHIER EXISTE
 * ─────────────────────────────────────────────────────────────────────────
 *
 * La V1 cherchait **côté serveur**, en JavaScript et non en SQL, pour une
 * raison consignée à l'étape 6 : « le `LIKE` de SQLite n'est insensible à la
 * casse que sur l'ASCII et ne connaît rien aux accents — *cafe* ne trouverait
 * pas *Café* ». Ce raisonnement tient toujours. Ce qui change en V2, c'est
 * qu'il faut aussi chercher **sans serveur** : le §3.1 promet « rechercher dans
 * la base personnelle, les recettes, les exercices » hors ligne.
 *
 * 🔴 **Deux implémentations du même classement finiraient par diverger, et le
 * symptôme serait le pire qui soit** : la même saisie renvoyant les mêmes
 * résultats *dans un autre ordre* selon qu'on a du réseau ou non. Personne ne
 * signale ce défaut — on se dit qu'on a mal tapé. D'où un seul jeu de
 * primitives, consommé par `server/src/routes/*.ts` **et** par
 * `web/src/lib/localSearch.ts`.
 *
 * ⚠️ **Un troisième fichier dans `shared/`, et c'est un écart assumé** — le §3
 * de l'archi V1 n'y prévoyait que `types.ts`. Même justification qu'à
 * `syncable.ts` (étape 1) : ce qui est ici est *structurellement* partagé, et
 * l'alternative — l'écrire dans `types.ts` — élargirait une brèche que ce
 * fichier-là demande explicitement de ne pas élargir (« du vocabulaire d'API,
 * aucune logique »).
 *
 * ⚠️ **Aucune dépendance, aucune connaissance du domaine.** Ce module ne sait
 * ni ce qu'est un aliment, ni ce qu'est un exercice : il compare des chaînes.
 * Ce sont les appelants qui décident *quels* champs interroger et dans quel
 * ordre — parce que cet ordre-là, lui, est propre à chaque domaine.
 */

/**
 * Forme comparable d'un texte : minuscules, sans accents, espaces resserrés.
 *
 * ⚠️ Repris **mot pour mot** de `routes/foods.ts` (étape 6) et de
 * `routes/recipes.ts` (étape 7), qui en portaient chacun une copie. Les deux
 * lisent désormais celle-ci : c'était déjà un doublon, il devenait un piège dès
 * qu'une troisième copie serait partie vivre côté client.
 *
 * `NFD` sépare les diacritiques de leur lettre, la plage `U+0300–U+036F` les
 * retire : « Café » → « cafe ». C'est ce que `LIKE` ne sait pas faire.
 *
 * ⚠️ La plage est écrite en **échappements**, jamais avec les caractères
 * combinants eux-mêmes : ceux-ci sont invisibles dans un éditeur et se
 * recomposent au premier outil de formatage qui passe.
 */
export function foldText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

/**
 * Score d'un champ **principal** (un nom), du meilleur au pire :
 *
 *   0 — commence par la recherche
 *   1 — la contient
 *   `null` — ne correspond pas
 *
 * ⚠️ `null` plutôt que `Infinity` ou `-1` : « ne correspond pas » n'est pas un
 * mauvais score, c'est l'absence de score. Le type force l'appelant à traiter
 * le cas plutôt qu'à le laisser filer dans un tri.
 */
export function scorePrimary(value: string, needle: string): number | null {
  const folded = foldText(value);
  if (folded.startsWith(needle)) return 0;
  if (folded.includes(needle)) return 1;
  return null;
}

/**
 * Score d'un champ **secondaire** (marque, tag, muscle, matériel) : 2 s'il
 * contient la recherche, `null` sinon.
 *
 * ⚠️ Un seul rang pour tous les champs secondaires, délibérément. Les
 * hiérarchiser entre eux (« la marque avant le muscle ») demanderait de
 * justifier chaque rang, et l'utilisateur ne perçoit pas la différence : ce
 * qu'il voit, c'est que les correspondances de nom sont en tête.
 */
export function scoreSecondary(value: string, needle: string): number | null {
  return foldText(value).includes(needle) ? 2 : null;
}

/**
 * Meilleur (donc plus petit) score parmi plusieurs champs secondaires.
 * `null` si aucun ne correspond.
 */
export function scoreAnySecondary(
  values: readonly (string | null | undefined)[],
  needle: string,
): number | null {
  for (const value of values) {
    if (value == null || value === '') continue;
    const score = scoreSecondary(value, needle);
    if (score !== null) return score;
  }
  return null;
}

/**
 * Comparateur d'appoint : à score égal, les favoris d'abord, puis l'ordre
 * alphabétique français.
 *
 * ⚠️ **`localeCompare(…, 'fr')`, jamais `<`.** La collation par défaut de
 * SQLite est binaire — toutes les majuscules avant toutes les minuscules — et
 * celle de JavaScript sur des chaînes brutes ne vaut pas mieux : « Œuf »
 * atterrirait après « Zeste ». Décision déjà prise pour les tags des recettes
 * (étape 7), reprise ici parce qu'elle vaut pour tout ce qui s'affiche en liste.
 */
export function compareScored<T>(
  a: { score: number; isFavorite: boolean; name: string; row: T },
  b: { score: number; isFavorite: boolean; name: string; row: T },
): number {
  if (a.score !== b.score) return a.score - b.score;
  if (a.isFavorite !== b.isFavorite) return a.isFavorite ? -1 : 1;
  return a.name.localeCompare(b.name, 'fr');
}

/**
 * Tri d'une liste **sans recherche** : favoris d'abord, puis alphabétique.
 * C'est l'ordre d'une bibliothèque qu'on parcourt plutôt qu'on n'interroge.
 */
export function compareByFavoriteThenName(
  a: { isFavorite: boolean; name: string },
  b: { isFavorite: boolean; name: string },
): number {
  if (a.isFavorite !== b.isFavorite) return a.isFavorite ? -1 : 1;
  return a.name.localeCompare(b.name, 'fr');
}
